import { useEffect, useState } from "react";
import type { GrassParams } from "./grassParams";

type Props = {
  paramsRef: React.MutableRefObject<GrassParams>;
};

type Field = {
  key: keyof GrassParams;
  label: string;
  min: number;
  max: number;
  step: number;
};

const FIELDS: Field[] = [
  { key: "windStrength", label: "Windstärke", min: 0, max: 1.5, step: 0.01 },
  { key: "windSpeed",    label: "Windgeschwindigkeit", min: 0, max: 3,   step: 0.01 },
  { key: "mouseRadius",  label: "Maus-Radius",  min: 0, max: 6,   step: 0.05 },
  { key: "fogDensity",   label: "Nebeldichte",   min: 0, max: 0.06, step: 0.001 },
];

export default function SettingsPanel({ paramsRef }: Props) {
  const [open, setOpen] = useState(false);
  // local mirror to drive the sliders
  const [, force] = useState(0);

  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => force((x) => x + 1), 250);
    return () => clearInterval(id);
  }, [open]);

  const handleChange = (key: keyof GrassParams, value: number) => {
    (paramsRef.current[key] as number) = value;
    force((x) => x + 1);
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Einstellungen"
        className="fixed bottom-6 right-6 z-[70] h-12 w-12 min-h-[3rem] min-w-[3rem] aspect-square shrink-0 flex items-center justify-center rounded-full bg-bg/80 backdrop-blur-md border border-line text-ink hover:text-sage hover:border-sage transition-colors cursor-pointer"
      >
        <i className={`text-lg ${open ? "ri-close-line" : "ri-settings-3-line"}`}></i>
      </button>

      <div
        className={`fixed bottom-24 right-6 z-[70] w-80 max-w-[calc(100vw-3rem)] rounded-2xl border border-line bg-bg/90 backdrop-blur-xl p-6 transition-all duration-300 origin-bottom-right ${
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-display text-lg text-ink">Graskonfigurator</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-inkDim mt-1">
              Stufe {paramsRef.current.stage + 1} von 7
            </div>
          </div>
          <span className="w-8 h-8 flex items-center justify-center rounded-full border border-moss/40 text-moss">
            <i className="ri-leaf-line text-sm"></i>
          </span>
        </div>

        <div className="space-y-5">
          {FIELDS.map((f) => {
            const val = paramsRef.current[f.key] as number;
            return (
              <div key={f.key}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs uppercase tracking-[0.2em] text-inkDim">
                    {f.label}
                  </label>
                  <span className="text-xs text-sage tabular-nums">
                    {val.toFixed(f.step < 0.01 ? 3 : 2)}
                  </span>
                </div>
                <input
                  type="range"
                  min={f.min}
                  max={f.max}
                  step={f.step}
                  value={val}
                  onChange={(e) => handleChange(f.key, parseFloat(e.target.value))}
                  className="w-full h-1 rounded-full bg-line accent-sage cursor-pointer"
                />
              </div>
            );
          })}
        </div>

        <div className="mt-6 pt-5 border-t border-line text-[11px] text-inkDim leading-relaxed">
          <i className="ri-information-line text-moss mr-1"></i>
          Ziehen Sie das Feld. Bewegen Sie den Cursor über die Seite - das Gras
          teilt sich, wo Sie vorbeigehen.
        </div>
      </div>
    </>
  );
}